import '@fontsource/familjen-grotesk/latin-500.css';
import '@fontsource/familjen-grotesk/latin-700.css';
import '@fontsource/overpass/latin-400.css';
import '@fontsource/overpass/latin-600.css';
import './brand.css';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandLanguageSwitch } from './BrandLanguageSwitch';

interface BrandAuthShellProps {
  /** Left column; only the login page has one. */
  hero?: ReactNode;
  children: ReactNode;
}

/**
 * Page frame for the console's pre-login pages (#2560): sign in, forgot
 * password, reset password and accept invitation use the fastcade.com brand
 * language (DESIGN.md Part I). All Part I styling hangs off the `.fc-brand`
 * root; see `./brand.css`. The root sets no `lang`: `<html lang>` is already
 * kept in sync by `src/i18n/index.ts`, and `:lang(zh)` inherits from it.
 */
export function BrandAuthShell({ hero, children }: BrandAuthShellProps) {
  const { t } = useTranslation();

  return (
    <div className="fc-brand">
      <header className="fc-header">
        <a className="fc-logo" href="https://fastcade.com/">
          {/* Same mark as site/src/components/Logo.astro. */}
          <svg width={30} height={18} viewBox="0 0 30 18" aria-hidden="true">
            <path
              d="M3 15 L12 4 L19 11 L27 3"
              style={{ stroke: 'var(--fc-rose)' }}
              strokeWidth={2.4}
              fill="none"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <circle cx={3} cy={15} r={3} style={{ fill: 'var(--fc-oxblood)' }} />
            <circle cx={27} cy={3} r={3} style={{ fill: 'var(--fc-oxblood)' }} />
          </svg>
          <span className="fc-wordmark">fastcade</span>
        </a>
        <BrandLanguageSwitch />
      </header>
      <main className="fc-main">
        {hero ? <section className="fc-hero">{hero}</section> : null}
        <section className="fc-side">{children}</section>
      </main>
      <footer className="fc-footer">
        <span>© {new Date().getFullYear()} Githon LLC</span>
        <a href={t('auth.shell.privacyHref')}>{t('auth.shell.privacy')}</a>
        <a href={t('auth.shell.termsHref')}>{t('auth.shell.terms')}</a>
      </footer>
    </div>
  );
}
